import { Box, Grid, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomFilledButton from "../../components/CustomFilledButton";
import CustomOutlinedButton from "../../components/CustomOutlined";
import {
  REACT_EMPLOYEE_LOGIN_SCREEN,
  REACT_FUNCTION_KEY_SCREEN,
  REACT_GENERAL_QUESTION,
  REACT_LABOR_CODE,
} from "../../constants/ReactEndPoints";
import CommonUtil from "../../util/CommonUtils";
import { Select } from "antd";
import { clearLocalStorage, removeLoggedInData } from "../../util/helperFunction";
import { useSelector } from "react-redux";
import { selectCurrentFunctionKey } from "../../feature/Dashboard/dashboardSelectors";
import { getAttestationList } from "../../services/FunctionKeysService";
import { useTranslation } from "react-i18next";

const Attestation = () => {
  const {t} = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    profileId,
    isMealLockoutAttestation = false,
    mealLockoutIssueType = 0,
  } = location?.state || {};

  const [attestationList, setAttestationList] = useState([]);
  const [question, setQuestion] = useState({});
  const [lastQuestion, setLastQuestion] = useState(false);
  const [attestationResponse, setAttestationResponse] = useState([]);
  const [customMappingObj, setCustomMappingObj] = useState({
    customV1: "",
    customV2: "",
    customV3: "",
    customV4: "",
    customV5: "",
    customV6: "",
    customV7: "",
    customV8: "",
    customV9: "",
    customV10: "",
  });

  const [punchPayload, setPunchPayload] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [dropdownList, setDropdownList] = useState([]);
  const selectedFunctionKeyObj = useSelector(selectCurrentFunctionKey);

  useEffect(() => {
    if (!CommonUtil.isTruthyValue(localStorage.getItem("isLoggedIn"))) {
      navigate(REACT_EMPLOYEE_LOGIN_SCREEN, { replace: true });
    }
  }, []);

  useEffect(() => {
    setPunchPayload(location?.state?.punchPayload || []);
  }, [location]);

  useEffect(() => {
    const fetchAttestationList = async () => {
      if (profileId <= 0) return;
      try {
        const res = await getAttestationList(profileId);
        const data = res?.data?.data?.[0];

        if (!CommonUtil.isEmpty(data)) {
          setAttestationList(data?.attestationList || []);
          setQuestion(data?.attestationList?.[0] || {});
          const dropDownList = data?.attestationList?.[0]?.dropDownList || [];
          setDropdownList(dropDownList);
          setSelectedAnswer(dropDownList[0] || "");
        }
      } catch (error) {
        console.error("Error fetching attestation list:", error);
      }
    };

    fetchAttestationList();
  }, [profileId]);

  useEffect(() => {
    if (question) {
      const mappedField = question?.mappedClockData;
      const firstDropDownItem = question?.dropDownList?.[0] || {};
      setSelectedAnswer(firstDropDownItem);
      setCustomMappingObj((prev) => ({
        ...prev,
        [mappedField]: firstDropDownItem?.dropDownAnswer || "",
      }));
    }
  }, [question]);

  const handleAnswerDropdownChange = (event) => {
    const selectedItem = dropdownList.find((item) => item.answerId === event.value);
    setSelectedAnswer(selectedItem ?? {});
    const question_mapped_field = question?.mappedClockData;
    setCustomMappingObj((prevMappingObj) => ({
      ...prevMappingObj,
      [question_mapped_field]: selectedItem?.dropDownAnswer,
    }));
  };

  //handle response button click
  const handleButtonAction = (btn, questionId) => {
    // Find the next question based on the selected button and current question
    let selectedButtonId = btn?.btnId;
    const nextQuestion = attestationList?.find(
      (item) =>
        item.parentButtonId === selectedButtonId &&
        item.questionId !== questionId &&
        item.parentQuestionId === questionId
    );

    // Handle punch rejection
    if (!nextQuestion && btn.btnAction === "REJECT_PUNCH") {
      enqueueSnackbar(t("attestation.messages.punchRejected"), { variant: "error" });
      if (isMealLockoutAttestation) {
        // Meal lockout reject → stay logged in, go back to dashboard
        setTimeout(() => {
          navigate(REACT_FUNCTION_KEY_SCREEN);
        }, 1000);
      } else {
        // Normal attestation reject → log out
        removeLoggedInData();
        setTimeout(() => {
          navigate(REACT_EMPLOYEE_LOGIN_SCREEN);
        }, 1000);
      }
      return;
    }

    // Handle cancellation action
    if (!nextQuestion && btn.btnAction === "CANCEL") {
      clearLocalStorage(["customMappingData"]);
      setTimeout(() => {
        navigate(REACT_FUNCTION_KEY_SCREEN);
      }, 1000);
      return;
    }

    // Find the current selected question from the attestation list
    const question = attestationList.find((item) => item.questionId === questionId);
    const mapped_field = question?.mappedClockData;

    // Update the custom mapping object: If the dropdown list is empty, set the  attestation profile set value into the corresponding custom mapped field.
    let updatedMappingObj = { ...customMappingObj };
    if (CommonUtil.isEmpty(dropdownList)) {
      updatedMappingObj = {
        ...customMappingObj,
        [mapped_field]: btn?.btnValue,
      };
      setCustomMappingObj(updatedMappingObj);
    }

    // Capture the attestation response
    const responseValue = !CommonUtil.isEmpty(selectedAnswer) ? selectedAnswer?.dropDownAnswer : btn?.btnValue;
    const buttonResponse = {
      attestation_action: btn?.btnAction,
      mapped_field: mapped_field,
      question_id: questionId,
      response_btn_id: btn?.btnId,
      response_value: responseValue,
    };

    // Update the attestation response state
    const updatedAttestationResponse = [...attestationResponse, buttonResponse];
    setAttestationResponse(updatedAttestationResponse);
    // If the next question exists, update state with it
    if (nextQuestion) {
      setQuestion(nextQuestion);
      setDropdownList(nextQuestion?.dropDownList);
      setSelectedAnswer(nextQuestion?.dropDownList[0]);
    } else {
      const newPunchPayload = [...punchPayload];
      if (newPunchPayload[0]) {
        newPunchPayload[0] = {
          ...newPunchPayload[0],
          attestationResponse: JSON.stringify(updatedAttestationResponse),
          ...updatedMappingObj,
          // Set issueType for meal lockout accepted punches
          ...(isMealLockoutAttestation && mealLockoutIssueType > 0 && {
            issueType: mealLockoutIssueType,
          }),
        };
        setPunchPayload(newPunchPayload);
        setLastQuestion(true);
      }
    }
  };

  useEffect(() => {
    if (!lastQuestion) return;

    if (!CommonUtil.isEmpty(selectedFunctionKeyObj?.codeLevels)) {
      navigate(REACT_LABOR_CODE, {
        state: { punchPayload, customMappingObj },
      });
    } else {
      navigate(REACT_GENERAL_QUESTION, {
        state: { punchPayload },
      });
    }
  }, [
    customMappingObj,
    lastQuestion,
    punchPayload,
    selectedFunctionKeyObj?.codeLevels,
    navigate,
  ]);

  return (
    <Grid
      container
      sx={{
        padding: "20px 30px 0px 30px",
        height: {
          sm: "calc(45vh)",
          md: "calc(50vh)",
          lg: "calc(55vh)",
          xl: "calc(55vh)",
        },
      }}
    >
      <Grid item container rowSpacing={2} alignContent={"flex-start"}>
        {/*Question title*/}
        <Grid item container>
          <Typography variant="attQuestionTitle">{question?.questionTitle}</Typography>
        </Grid>

        {/* Question text */}
        <Grid item container alignContent={"flex-start"}>
          <Typography variant="attQuestionText">{question?.questionText}</Typography>
        </Grid>

        {/* Answer dropdown selection field */}
        {!CommonUtil.isEmpty(dropdownList) && (
          <Grid item container justifyContent={"center"}>
            <Select
              placeholder={t("attestation.placeholders.dropdown")}
              value={selectedAnswer?.answerId}
              onChange={handleAnswerDropdownChange}
              size="large"
              style={{
                width: "40%",
              }}
              options={dropdownList.map((item) => ({
                key: item.answerId,
                value: item.answerId,
                label: item.dropDownAnswer,
              }))}
              labelInValue
            />
          </Grid>
        )}
      </Grid>

      {/* Question response button */}
      <Box sx={{ position: "absolute", right: "15px", bottom: "20px" }}>
        {question?.btnDetails?.map((btn) =>
          btn.btnText !== "YES" ? (
            <CustomOutlinedButton onBtnClick={() => handleButtonAction(btn, question.questionId)} title={btn.btnText} />
          ) : (
            <CustomFilledButton onBtnClick={() => handleButtonAction(btn, question.questionId)} title={btn.btnText} />
          )
        )}
      </Box>
    </Grid>
  );
};

export default Attestation;
